import { Injectable } from '@angular/core';
import { BcmService } from '@modules/bcm/bcm.service';
import { Tenant } from '@shared/models/tenant';
import { TseFiscalClient } from '@shared/models/tse/TseFiscalClient';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TseFiscalResponse } from '@shared/models/tse/TseFiscalResponse';
import { TsePayment } from '@shared/models/tse/TsePayment';
import { UserService } from '@core/services/user.service';
import { TseDocumentType } from '@shared/models/tse/TseDocumentType';
import { TseDocument } from '@shared/models/tse/TseDocument';
import { TsePaymentType } from '@shared/models/tse/TsePaymentType';
import { TseDocumentPositionType } from '@shared/models/tse/TseDocumentPositionType';
import { TseBusinessTransactionType } from '@shared/models/tse/TseBusinessTransactionType';
import { TseInformationApiService } from '@bcmApiServices/tse/tse-information.api-service';
import { v4 as uuidv4 } from 'uuid';
import { BcmCashRegister } from '@shared/models/bcm-cash-register';
import { BcmCashRegistersApiService } from '@bcmApiServices/bcm-cash-registers-api.service';
import { BcmReceipt } from '@shared/models/bcm-receipt';
import { StaticGlobals } from '@modules/bcm/@shared/static-globals';
import { InvoicePosition } from '@shared/models/invoice-position';
import { BcmPaymentTypeFromString, Person } from '@shared/models/person';
import { Company } from '@shared/models/company';
import { BcmFinancialRecordPdfData } from '@shared/models/bcm-financial-record';
import { TseReferenceType } from '@shared/models/tse/TseReferenceType';
import * as countries from 'i18n-iso-countries';
import deLocale from 'i18n-iso-countries/langs/de.json';
import { TsePartnerIdentificationType } from '@shared/models/tse/TsePartnerIdentificationType';
import { TsePartnerType } from '@shared/models/tse/TsePartnerType';
import { BcmInvoiceWdTemplate } from '@shared/models/bcm-invoice-wd-template';
import { getTaxRateValue } from '@core/functions/get-tax-rate-value';
import { roundCurrency, roundNumber, RoundNumberFactor } from '@modules/bcm/@shared/pipes/dynamic-price-rounded.pipe';
import { Invoice, InvoiceAddressStructured } from '@shared/models/invoice';
import { TsePartner } from '@shared/models/tse/TsePartner';
import { TseApiService } from '@bcmApiServices/tse-api.service';
import { BcmCashRegisterUser } from '@shared/models/bcm-cash-register-user';
import { TseAutomaticVatCalculation } from '@shared/models/tse/TseAutomaticVatCalculation';
import { Country } from '@shared/models/country';
import { getAddressString } from '@shared/functions/get-address-string';
import { getPriceDataFromInvoicePositions } from '@shared/functions/getPriceDataFromInvoicePositions';
import { enrichInvoicePositionsWithTseVatIdentification } from './enrich-invoice-positions-from-tse-document';
import { BcmTenantService } from '@modules/bcm/bcm-tenant.service';
import { BcmTenantPermission } from '@modules/bcm/bcm-tenant-permission';
import { TsePaymentContext } from '@modules/bcm/accounting/invoices/dialogs/tse-payment-flow.util';

@Injectable({
    providedIn: 'root'
})
export class CashRegisterService {

    private _tenant: Tenant;
    private readonly _userId: string;
    private readonly _userFullName: string;

    private readonly _userFirstName: string;
    private readonly _userLastName: string;

    constructor(
        _bcmService: BcmService,
        private _userService: UserService,
        private _cashRegisterApi: BcmCashRegistersApiService,
        private _tseApiService: TseApiService,
        private _tseInformationService: TseInformationApiService,
        private _bcmTenantService: BcmTenantService,
    ) {
        this._tenant = _bcmService.tenant;
        this._userId = this._userService.user.emailHash;
        this._userFullName = this._userService.userFullName;
        this._userFirstName = this._userService.userFirstName;
        this._userLastName = this._userService.userLastName;

        countries.registerLocale(deLocale);
    }

    get userFullName(): string {
        return this._userFullName;
    }

    getFiscalClient(cashRegister: BcmCashRegister): Observable<TseFiscalClient> {
        return this._tseApiService.getClient(cashRegister);
    }

    createCashRegister(storeNumber: string, terminalNumber: string, title: string, note: string, cashRegisterUsers: BcmCashRegisterUser[]): Observable<BcmCashRegister> {
        return this._tseApiService.createCashRegister(storeNumber, terminalNumber, title, note, cashRegisterUsers);
    }

    openCashRegister(cashRegister: BcmCashRegister, amount: number): Observable<void> {
        return this._tseApiService.openCashRegister(cashRegister, amount);
    }

    // Hinweis: Reihenfolge differenceSum/differenceReason bewusst anders als in TseApiService –
    // Aufrufer (close-cash-register-dialog) übergeben in dieser Reihenfolge.
    closeCashRegister(cashRegister: BcmCashRegister, amount: number,
                      differenceSum: number = 0, differenceReason: string | null = null,
                      cashAdd: number, cashLift: number, cashLiftReason: string | null = null,
                      closeMonth: boolean = false): Observable<void> {

        return this._tseApiService.closeCashRegister(cashRegister, amount, differenceReason, differenceSum, cashAdd, cashLift, cashLiftReason, closeMonth);
    }


    bookCashLift(cashRegister: BcmCashRegister, amount: number): Observable<TseFiscalResponse> {
        return this._tseApiService.bookCashLift(cashRegister, amount);
    }

    getActualStock(cashRegister: BcmCashRegister): Observable<TsePayment[]> {
        return this._tseApiService.getActualStock(cashRegister);
    }

    getLoggedInCashRegisterForUser(): Observable<BcmCashRegister> {
        return this._cashRegisterApi.getLoggedInCashRegisterForUser();
    }

    /**
     * Einzige Quelle für den TSE-Zustand der Zahlungsdialoge (#6950): Add-on des Mandanten und
     * angemeldete Kasse getrennt. Schlägt die Kassenabfrage fehl, bleibt der Mandant ein
     * TSE-Mandant – nur so kann keine Kartenzahlung an der TSE vorbei gebucht werden.
     */
    getTsePaymentContext(): Observable<TsePaymentContext> {
        if (!this._bcmTenantService.checkPermission(BcmTenantPermission.CASH)) {
            return of({hasTseAvailable: false, cashRegister: null});
        }

        return this.getLoggedInCashRegisterForUser().pipe(
            map(cashRegister => ({hasTseAvailable: true, cashRegister: cashRegister || null})),
            catchError(() => of({hasTseAvailable: true, cashRegister: null})),
        );
    }

    addCancellationReceipt(
        cashRegister: BcmCashRegister,
        receiptToCancel: BcmReceipt,
        paymentType: TsePaymentType,
        address: string,
        person?: Person,
        company?: Company,
        wordTemplate?: BcmInvoiceWdTemplate,
        addPositionsBackToCoaster?: boolean,
        cancellationReason?: string,
        addressStructured?: InvoiceAddressStructured): Observable<BcmReceipt> {
        const positions = receiptToCancel.positions.map(p => new InvoicePosition({...p, price: p.price * (-1)} as any));
        return this.addReceipt(
            cashRegister,
            positions,
            paymentType,
            address,
            person,
            company,
            wordTemplate,
            `STORNO für Beleg ${receiptToCancel.receiptNumber}`,
            receiptToCancel,
            addPositionsBackToCoaster,
            addressStructured,
            cancellationReason);
    }


    addReceipt(cashRegister: BcmCashRegister,
               positions: InvoicePosition[],
               paymentType: TsePaymentType,
               address: string,
               person?: Person,
               company?: Company,
               wordTemplate?: BcmInvoiceWdTemplate,
               note?: string,
               receiptToCancel?: BcmReceipt,
               addPositionsBackToCoaster?: boolean,
               addressStructured?: InvoiceAddressStructured,
               cancellationReason?: string): Observable<BcmReceipt> {

        if (!positions?.length || positions.length < 1) {
            return throwError(() => [`Es wurden keine Positionen angegeben.`]);
        }

        /*
        for (const position of positions) {
            if (position.quantity < 1) {
                return throwError(() => [`Positionen mit Menge = 0 können nicht auf den Barbeleg übernommen werden.`]);
            }
        }*/

        const totalAmount = this.getTotal(positions, 1);
        const isCancellation = !!(receiptToCancel?.id);

        if (isCancellation) {
            const reference = receiptToCancel?.tseDocument;
            if (!reference?.bookDate || !reference?.documentId || !reference?.documentGuid || !reference?.documentNumber || !reference?.fiscalDocumentNumber) {
                return throwError(() => ['Das zu stornierende Dokument enthält keine vollständigen TSE-Referenzdaten.']);
            }
        }

        const date = new Date();

        if (isCancellation) {
            enrichInvoicePositionsWithTseVatIdentification(positions, receiptToCancel.tseDocument);
        }

        // Fetch vatIdentification for each unique tax rate that isn't already set on the position.
        // The RetailForce API rejects documents with missing vatIdentification.
        const uniqueTaxRates = [
            ...new Set(
                positions
                    .filter(p => p.tseVatIdentificationId == null)
                    .map(p => getTaxRateValue(p))
            )
        ];

        const vatLookup$: Observable<Map<number, number>> = uniqueTaxRates.length > 0
            ? forkJoin(
                uniqueTaxRates.map(taxRate =>
                    this._tseInformationService
                        .getVatIdentification(cashRegister.clientId, taxRate, date)
                        .pipe(map(vatId => ({taxRate, vatId})))
                )
            ).pipe(map(results => {
                const vatMap = new Map<number, number>();
                results.forEach(r => vatMap.set(r.taxRate, r.vatId));
                return vatMap;
            }))
            : of(new Map<number, number>());

        return vatLookup$.pipe(
            switchMap(vatMap => {
                const partnerCountryInput = person?.country?.de
                    || company?.country?.de
                    || person?.countryCode
                    || company?.countryCode
                    || addressStructured?.country;
                const partnerCountryCode = this._tryResolveAlpha3CountryCode(partnerCountryInput);
                if (Math.abs(totalAmount) > 250 && !partnerCountryCode) {
                    return throwError(() => ['Dem Empfänger ist kein gültiges Land hinterlegt.']);
                }

                const missingVatPosition = positions.find(position => {
                    const taxRateValue = getTaxRateValue(position);
                    return !(position.tseVatIdentificationId ?? vatMap.get(taxRateValue));
                });

                if (missingVatPosition) {
                    const taxRateValue = getTaxRateValue(missingVatPosition);
                    return throwError(() => [
                        `Keine TSE-Steuerkennung für MwSt.-Satz ${taxRateValue}% gefunden. Bitte TSE-Konfiguration prüfen.`
                    ]);
                }

                const document: TseDocument = {
                    cancellationDocument: !!(receiptToCancel?.id),
                    documentReference: (receiptToCancel?.id) ? {
                        documentType: TseDocumentType.RECEIPT,
                        documentBookDate: receiptToCancel.tseDocument.bookDate,
                        documentId: receiptToCancel.tseDocument.documentId,
                        documentGuid: receiptToCancel.tseDocument.documentGuid,
                        referenceType: TseReferenceType.CANCELLATION,
                        documentNumber: receiptToCancel.tseDocument.documentNumber,
                        fiscalDocumentNumber: receiptToCancel.tseDocument.fiscalDocumentNumber
                    } : null,
                    documentGuid: uuidv4(),
                    documentId: 'receiptIdentifier',
                    documentNumber: 'receiptIdentifier',
                    fiscalDocumentNumber: 1,
                    FiscalDocumentStartTime: date.getTime(),
                    fiscalDocumentRevision: 1,
                    isTraining: false,
                    positionCount: positions.length,
                    uniqueClientId: cashRegister.clientId,
                    createDate: date.toUTCString(),
                    bookDate: date.toUTCString(),
                    documentType: TseDocumentType.RECEIPT,
                    user: {
                        id: this._userId,
                        caption: this._userFullName,
                        firstName: this._userFirstName,
                        lastName: this._userLastName
                    },
                    partner: this._buildTsePartner(totalAmount, person, company, addressStructured),
                    positions: positions.map((position, index) => {
                        const taxRateValue = getTaxRateValue(position);
                        const vatIdentification = (position.tseVatIdentificationId ?? vatMap.get(taxRateValue)) as number;

                        const additionalFields: Record<string, string> = {};

                        // RF container throws 500 error, if some additionalField values are
                        // null or undefined or anything else but string.
                        if (position.account) {
                            additionalFields.account = String(position.account);
                        }

                        if (position.unit?.name) {
                            additionalFields.unit = String(position.unit.name);
                        }

                        if (position.product?.name || position.title) {
                            additionalFields.productName = String(position.product?.name || position.title);
                        }

                        return {
                            itemCaption: position.title,
                            itemShortCaption: null,
                            discounts: [],
                            type: TseDocumentPositionType.ITEM,
                            useSubItemVatCalculation: true,
                            subItems: null,
                            inHouse: true,
                            quantity: isCancellation ? -Math.abs(position.quantity) : position.quantity,
                            quantityUnit: null,
                            itemId: position.itemNumber ||
                                (position.product?.id ? (position.product?.itemNumber || 'ID_' + String(position.product.id)) : 'UUID_' + uuidv4()),
                            baseNetValue: this._getNetValue(position),
                            baseGrossValue: this._getGrossValue(position),
                            baseTaxValue: this._getTaxValue(position),
                            businessTransactionType: TseBusinessTransactionType.REVENUE,
                            vatIdentification,
                            vatPercent: taxRateValue,
                            netValue: this._getNetValue(position),
                            grossValue: this._getGrossValue(position),
                            taxValue: this._getTaxValue(position),
                            accountingIdentifier: null,
                            positionNumber: index,
                            positionReference: null,
                            cancellationPosition: false,
                            additionalFields
                        };
                    }),
                    payments: [
                        {
                            amount: totalAmount,
                            currencyIsoCode: 'EUR',
                            caption: 'PAYMENT',
                            uniqueReadablePaymentIdentifier: `RECEIPT-${new Date().getTime()}-${cashRegister.clientId}`,
                            foreignAmount: 0.0,
                            foreignAmountExchangeRate: 0.0,
                            paymentType
                        }
                    ],
                };

                const priceData = getPriceDataFromInvoicePositions(positions);

                const pdfData: BcmFinancialRecordPdfData = {
                    wordTemplate,
                    address: address,
                    bcmSettings: null,
                    date: null,
                    debtorNumber: person?.identNumber || company?.identNumber,
                    iban: person?.IBAN || company?.IBAN,
                    invoiceLogo: null,
                    invoiceNumber: null,
                    paymentType: BcmPaymentTypeFromString[paymentType],
                    positions: positions,
                    priceData: priceData,
                    tenant: null,
                    tenantRelation: null,
                    note
                };

                return this._tseApiService.createDocument(
                    cashRegister,
                    document,
                    positions,
                    totalAmount,
                    priceData.totalTax,
                    pdfData,
                    paymentType,
                    person,
                    company,
                    receiptToCancel,
                    addPositionsBackToCoaster,
                    cancellationReason
                );
            })
        );

    }

    addInvoicePayIn(cashRegister: BcmCashRegister,
                    invoice: Invoice,
                    paymentType: TsePaymentType,
                    amount: number,
                    addressData?: {
                        fullName?: string,
                        street?: string,
                        houseNumber?: string,
                        postCode?: string,
                        city?: string,
                        country?: Country,
                    }): Observable<BcmReceipt> {
        return this._addInvoiceCashTransfer('in', cashRegister, invoice, paymentType, amount, addressData);
    }

    /** Barauszahlung einer Gutschrift: TSE PayOut mit negativem Betrag (Geld verlässt die Kasse) */
    addInvoicePayOut(cashRegister: BcmCashRegister,
                     invoice: Invoice,
                     paymentType: TsePaymentType,
                     amount: number,
                     addressData?: {
                         fullName?: string,
                         street?: string,
                         houseNumber?: string,
                         postCode?: string,
                         city?: string,
                         country?: Country,
                     }): Observable<BcmReceipt> {
        return this._addInvoiceCashTransfer('out', cashRegister, invoice, paymentType, amount, addressData);
    }

    private _addInvoiceCashTransfer(direction: 'in' | 'out',
                                    cashRegister: BcmCashRegister,
                                    invoice: Invoice,
                                    paymentType: TsePaymentType,
                                    amount: number,
                                    addressData?: {
                                        fullName?: string,
                                        street?: string,
                                        houseNumber?: string,
                                        postCode?: string,
                                        city?: string,
                                        country?: Country,
                                    }): Observable<BcmReceipt> {

        const isPayOut = direction === 'out';
        const signedAmount = isPayOut ? -Math.abs(amount) : amount;
        const caption = `${isPayOut ? 'Auszahlung' : 'Einzahlung'} ${invoice.invoiceNumber}`;
        const identifierPrefix = isPayOut ? 'PAY_OUT' : 'PAY_IN';

        const date = new Date();
        const requiresAddress = Math.abs(amount) > 250;

        const person = invoice.person;
        const company = invoice.company;
        const partnerCountryInput = person?.country?.de
            || company?.country?.de
            || person?.countryCode
            || company?.countryCode
            || addressData?.country?.de;
        const resolvedPartnerCountryCode = this._tryResolveAlpha3CountryCode(partnerCountryInput);

        if (requiresAddress && !resolvedPartnerCountryCode) {
            return throwError(() => ['Dem Empfänger ist kein gültiges Land hinterlegt.']);
        }

        const hasPartnerData = !!(
            person?.id
            || company?.id
            || addressData?.fullName
            || ((addressData?.street || addressData?.houseNumber) && addressData?.city)
        );

        const address = (person || company)?.getAddressString('\n', true, true, true)
            || getAddressString(addressData)
            || invoice?.address;

        const document: TseDocument = {
            documentGuid: uuidv4(),
            documentId: 'receiptIdentifier',
            documentNumber: 'receiptIdentifier',
            automaticVatCalculation: TseAutomaticVatCalculation.NO_CALCULATION,
            fiscalDocumentNumber: 1,
            FiscalDocumentStartTime: date.getTime(),
            fiscalDocumentRevision: 1,
            isTraining: false,
            positionCount: 1,
            uniqueClientId: cashRegister.clientId,
            createDate: date.toUTCString(),
            bookDate: date.toUTCString(),
            documentType: isPayOut ? TseDocumentType.PAY_OUT : TseDocumentType.PAY_IN,
            user: {
                id: this._userId,
                caption: this._userFullName,
                firstName: this._userFirstName,
                lastName: this._userLastName
            },
            partner: (requiresAddress && hasPartnerData) ?
                {
                    id: `${person?.id || company?.id}-${person?.fullName || company?.fullName || addressData?.fullName}`,
                    caption: person?.fullName || company?.fullName || addressData?.fullName,
                    street: person?.streetWithoutNumber || company?.streetWithoutNumber || addressData?.street,
                    streetNumber: person?.streetNumber || company?.streetNumber || addressData?.houseNumber,
                    city: person?.city || company?.city || addressData?.city,
                    postalCode: person?.postCode || company?.postCode || addressData?.postCode,
                    countryCode: resolvedPartnerCountryCode || 'DEU',
                    identificationType: TsePartnerIdentificationType.OTHER,
                    partnerType: TsePartnerType.CUSTOMER,
                    partnerClassification: 'Customer'
                } :
                null,
            positions: [
                {
                    caption,
                    itemCaption: caption,
                    quantity: 1,
                    grossValue: signedAmount,
                    baseGrossValue: signedAmount,
                    businessTransactionType: isPayOut ? TseBusinessTransactionType.PAY_OUT : TseBusinessTransactionType.PAY_IN,
                    itemId: 'UUID_' + uuidv4(),
                    netValue: signedAmount,
                    taxValue: 0,
                    vatIdentification: 5,
                    type: TseDocumentPositionType.BOOKING
                }
            ],
            payments: [
                {
                    amount: signedAmount,
                    currencyIsoCode: 'EUR',
                    caption: identifierPrefix,
                    uniqueReadablePaymentIdentifier: `${identifierPrefix}-${invoice.invoiceNumber}-${new Date().getTime()}-${cashRegister.clientId}`,
                    foreignAmount: 0.0,
                    foreignAmountExchangeRate: 0.0,
                    paymentType
                }
            ]
        };

        const pdfData: BcmFinancialRecordPdfData = {
            address: address,
            bcmSettings: null,
            date: null,
            debtorNumber: person?.identNumber || company?.identNumber,
            iban: person?.IBAN || company?.IBAN,
            invoiceLogo: null,
            invoiceNumber: null,
            paymentType: BcmPaymentTypeFromString[paymentType],
            positions: [
                // @ts-ignore
                {
                    title: caption,
                    price: signedAmount,
                    invoice: invoice,
                    quantity: 1,
                }
            ],
            priceData: {
                totalTaxes: [],
                totalTax: 0,
                totalGrossPrice: signedAmount,
                totalNetPrice: signedAmount,
                totalTaxFreeProductsPrice: signedAmount
            },
            tenant: null,
            tenantRelation: null,
        };

        return this._tseApiService.createDocument(
            cashRegister,
            document,
            [],
            signedAmount,
            0,
            pdfData,
            paymentType,
            person,
            company,
        );
    }


    // incrementReceiptReprintCounter(cashRegister: BcmCashRegister, receipt: BcmReceipt): Observable<BcmReceipt> {
    //     const diff = Math.abs((receipt.insertedOn || new Date()).getTime() - new Date().getTime());
    //     const diffDays = Math.ceil(diff / (1000 * 3600 * 24));
    //
    //     const daysBack = diffDays > 0 ? diffDays : -1;
    //
    //     const user = {
    //         id: this._userId,
    //         caption: this._userFullName,
    //         firstName: this._userFirstName,
    //         lastName: this._userLastName
    //     };
    //
    //     return this._cashRegisterApi.incrementReprintCounter(cashRegister, receipt.id);
    //
    //     // tse api call not working at the moment
    //     // return this._transactionsApi.incrementReprintCount(cashRegister.clientId, receipt.tseDocument.documentGuid, user, daysBack)
    //     //     .pipe(switchMap(() => this._cashRegisterApi.incrementReprintCounter(cashRegister, receipt.id)));
    // }

    private _round(value: number): number {
        return StaticGlobals.roundTo5Cent
            ? roundNumber(value, RoundNumberFactor.Swiss5CentimesRounding)
            : roundNumber(value, RoundNumberFactor.TwoDecimals);
    }

    private _getGrossValue(position: InvoicePosition): number {
        if (!StaticGlobals.netPrices) {
            // totalPrice already includes quantity (price × qty − discount)
            return this._round(position.totalPrice);
        } else {
            const taxRateValue = getTaxRateValue(position);
            return this._round(this._getNetValue(position) * (1 + taxRateValue / 100));
        }
    }

    private _getNetValue(position: InvoicePosition): number {
        const taxRateValue = getTaxRateValue(position);
        if (StaticGlobals.netPrices) {
            return this._round(position.totalPrice);
        } else {
            return this._round(this._getGrossValue(position) / (1 + taxRateValue / 100));
        }
    }

    private _getTaxValue(position: InvoicePosition): number {
        return this._getGrossValue(position) - this._getNetValue(position);
    }

    public getTotal(positions: InvoicePosition[], sign: number): number {
        const priceData = getPriceDataFromInvoicePositions(positions, sign);

        return roundCurrency(priceData.totalGrossPrice);
    }

    private _resolveAlpha3CountryCode(countryInput: string | undefined | null): string {
        return resolveAlpha3CountryCode(countryInput);
    }

    private _tryResolveAlpha3CountryCode(countryInput: string | undefined | null): string | undefined {
        return tryResolveAlpha3CountryCode(countryInput);
    }

    private _buildTsePartner(
        totalAmount: number,
        person?: Person,
        company?: Company,
        addressStructured?: InvoiceAddressStructured,
    ): TsePartner | null {
        if (Math.abs(totalAmount) <= 250) {
            return null;
        }

        if (person?.id || company?.id) {
            const streetParts = (addressStructured?.street || '').trim().split(' ');
            const fallbackStreetNumber = streetParts.length > 1 ? streetParts[streetParts.length - 1] : '';
            const fallbackStreetWithout = streetParts.length > 1
                ? streetParts.slice(0, streetParts.length - 1).join(' ')
                : (addressStructured?.street || '');

            return {
                id: `${person?.id || company?.id}-${person?.fullName || company?.fullName}`,
                caption: person?.fullName || company?.fullName,
                street: person?.streetWithoutNumber || company?.streetWithoutNumber || fallbackStreetWithout,
                streetNumber: person?.streetNumber || company?.streetNumber || fallbackStreetNumber,
                city: person?.city || company?.city || addressStructured?.city || '',
                postalCode: person?.postCode || company?.postCode || addressStructured?.zip || '',
                countryCode: this._resolveAlpha3CountryCode(
                    person?.country?.de
                    || company?.country?.de
                    || person?.countryCode
                    || company?.countryCode
                    || addressStructured?.country
                ),
                identificationType: TsePartnerIdentificationType.OTHER,
                partnerType: TsePartnerType.CUSTOMER,
                partnerClassification: 'Customer',
            };
        }

        if (addressStructured?.lastName || addressStructured?.firstName ||
            (addressStructured?.street && addressStructured?.city)) {
            const streetParts = (addressStructured.street || '').trim().split(' ');
            const streetNumber = streetParts.length > 1 ? (streetParts[streetParts.length - 1] || '') : '';
            const streetWithoutNumber = streetParts.length > 1
                ? streetParts.slice(0, streetParts.length - 1).join(' ')
                : (addressStructured.street || '');
            const nameKey = addressStructured.lastName || addressStructured.firstName || addressStructured.city || 'anon';
            const caption = [addressStructured.firstName, addressStructured.lastName].filter(Boolean).join(' ')
                || addressStructured.city || '';

            return {
                id: `manual-${nameKey}`,
                caption,
                street: streetWithoutNumber,
                streetNumber,
                city: addressStructured.city || '',
                postalCode: addressStructured.zip || '',
                countryCode: this._resolveAlpha3CountryCode(addressStructured.country),
                identificationType: TsePartnerIdentificationType.OTHER,
                partnerType: TsePartnerType.CUSTOMER,
                partnerClassification: 'Customer',
            };
        }

        return null;
    }

}

/**
 * Löst einen beliebigen Länder-String in einen ISO-3166-1-Alpha-3-Code auf.
 * Akzeptiert: deutschen Ländernamen ("Deutschland"), Alpha-2 ("DE") und Alpha-3 ("DEU").
 * Gibt 'DEU' zurück, wenn der Wert leer oder nicht auflösbar ist.
 * Exportiert für Unit-Tests.
 */
export function resolveAlpha3CountryCode(countryInput: string | undefined | null): string {
    return tryResolveAlpha3CountryCode(countryInput) || 'DEU';
}

export function tryResolveAlpha3CountryCode(countryInput: string | undefined | null): string | undefined {
    if (!countryInput || !countryInput.trim()) {
        return undefined;
    }
    const trimmed = countryInput.trim();

    // Bereits ein Alpha-3-Code?
    if (/^[A-Za-z]{3}$/.test(trimmed) && countries.getName(trimmed.toUpperCase(), 'de')) {
        return trimmed.toUpperCase();
    }

    // Alpha-2-Code → Alpha-3 umwandeln
    if (/^[A-Za-z]{2}$/.test(trimmed)) {
        const alpha3 = countries.alpha2ToAlpha3(trimmed.toUpperCase());
        if (alpha3) { return alpha3; }
    }

    // Deutschen Ländernamen → Alpha-3
    const alpha3FromDe = countries.getAlpha3Code(trimmed, 'de');
    if (alpha3FromDe) { return alpha3FromDe; }

    // Englischen Ländernamen → Alpha-3 (Fallback für gemischte Eingaben)
    const alpha3FromEn = countries.getAlpha3Code(trimmed, 'en');
    if (alpha3FromEn) { return alpha3FromEn; }

    return undefined;
}
